import { useEffect, useRef, useState } from "react";
import { LoaderCircle, MapPin, Search } from "lucide-react";
import { api } from "@/lib/api";
import { LOCATION_PRESETS } from "@/lib/locations";
import { inputCls } from "@/components/TopBar";

/** Search-and-set place picker backed by /api/geocode (OpenStreetMap), with quick presets. */
export const LocationSearch = ({ label, value, onChange, testid }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = (place) => {
    onChange(place);
    setQ("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid={testid}>
      <label className="overline mb-2 block">{label}</label>
      <div className="flex items-center gap-2 border border-white/15 bg-[#121212] px-3 py-2 focus-within:border-[#00F0FF]">
        <Search size={12} className="shrink-0 text-[#52525B]" />
        <input
          data-testid={`${testid}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="SEARCH ANY PLACE…"
          className="w-full bg-transparent text-xs outline-none placeholder:text-[#3f3f46]"
        />
        {loading && <LoaderCircle size={12} className="animate-spin text-[#00F0FF]" />}
      </div>

      {value ? (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-[#00F0FF]" data-testid={`${testid}-selected`}>
          <MapPin size={11} className="mt-0.5 shrink-0" />
          <span className="line-clamp-2">{value.name}</span>
        </p>
      ) : (
        <p className="overline mt-2">nothing selected</p>
      )}

      {open && results.length > 0 && (
        <div
          data-testid={`${testid}-results`}
          className="doot-scroll absolute z-[50] mt-1 max-h-56 w-full overflow-y-auto border border-white/15 bg-[#0A0A0A]"
        >
          {results.map((r, i) => (
            <button
              type="button"
              key={`${r.lat}-${r.lng}-${i}`}
              data-testid={`${testid}-result-${i}`}
              onClick={() => pick(r)}
              className="block w-full border-b border-white/5 px-3 py-2 text-left text-[11px] text-[#A1A1AA] hover:bg-[#1A1A1A] hover:text-[#00F0FF]"
            >
              {r.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        {LOCATION_PRESETS.slice(0, 6).map((p) => (
          <button
            type="button"
            key={p.name}
            data-testid={`${testid}-preset-${p.name.split(",")[0].toLowerCase()}`}
            onClick={() => pick(p)}
            className="border border-white/10 px-1.5 py-0.5 text-[9px] tracking-[0.1em] text-[#52525B] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            {p.name.split(",")[0].toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
};

export const rawInputCls = inputCls;
